import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdPerson,
  MdWarning,
  MdRefresh,
  MdSearch,
  MdVisibility,
  MdTrendingDown,
  MdWorkOff,
  MdGroup,
  MdFilterList,
  MdDownload
} from 'react-icons/md';
import UsibrasLoader from '../../../components/UsibrasLoader';
import UsibrasToast from '../../../components/UsibrasToast';
import { tokenManager } from '../../../utils/tokenManager';

const RiskReport = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [periodType, setPeriodType] = useState('week'); // week, month, etc.
  const [searchName, setSearchName] = useState('');
  const [highRiskWorkers, setHighRiskWorkers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [riskCriteria, setRiskCriteria] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [cacheKey, setCacheKey] = useState('');
  const currentDataRef = useRef({ high_risk_workers: [], summary: null, risk_criteria: '' });

  // Helper functions
  const formatDateOnly = (date) => date.toISOString().split('T')[0];

  // Cache key based on period type
  const getCacheKey = (period) => `risk_report_cache_${period}`;

  // Load cached data from localStorage
  const loadCachedData = (period) => {
    try {
      const key = getCacheKey(period);
      const cached = localStorage.getItem(key);
      if (cached) {
        const parsedCache = JSON.parse(cached);
        // Check if cache is still valid (not older than 5 minutes)
        const cacheAge = Date.now() - (parsedCache.timestamp || 0);
        const fiveMinutes = 5 * 60 * 1000;
        
        if (cacheAge < fiveMinutes) {
          const cachedWorkers = parsedCache.high_risk_workers || [];
          const cachedSummary = parsedCache.summary || null;
          const cachedRiskCriteria = parsedCache.risk_criteria || '';
          setHighRiskWorkers(cachedWorkers);
          setSummary(cachedSummary);
          setRiskCriteria(cachedRiskCriteria);
          // Update ref
          currentDataRef.current = {
            high_risk_workers: cachedWorkers,
            summary: cachedSummary,
            risk_criteria: cachedRiskCriteria,
          };
          return true;
        }
      }
    } catch (error) {
      console.error('Error loading cached data:', error);
    }
    return false;
  };

  // Save data to cache
  const saveToCache = (period, workers, summaryData, riskCriteriaData) => {
    try {
      const key = getCacheKey(period);
      const cacheData = {
        high_risk_workers: workers,
        summary: summaryData,
        risk_criteria: riskCriteriaData,
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  };

  // Check if data has changed
  const hasDataChanged = (oldData, newData) => {
    if (!oldData || !newData) return true;
    const oldIds = (oldData.high_risk_workers || []).map(w => w.id).sort().join(',');
    const newIds = (newData.high_risk_workers || []).map(w => w.id).sort().join(',');
    return oldIds !== newIds || 
           oldData.summary?.high_risk_workers_count !== newData.summary?.high_risk_workers_count;
  };

  // Fetch high risk workers from API
  const fetchRiskData = async (showLoader = true, isBackground = false) => {
    if (showLoader) {
      setIsLoading(true);
    } else {
      setIsBackgroundLoading(true);
    }
    
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const url = `https://usibras-web-backend.onrender.com/api/risk-status/high-risk?period_type=${periodType}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to load risk data');
      }

      const workers = result.high_risk_workers || [];
      
      // Map API data to match table structure
      const mappedWorkers = workers.map(worker => ({
        id: worker.id,
        worker_id: worker.id,
        card_number: worker.card_number,
        name: worker.name,
        group_name: worker.table_number,
        group: worker.table_number,
        daily_target: worker.daily_target,
        period_target: worker.performance?.period_target || 0,
        actual_output: worker.performance?.actual_output || 0,
        average_output: worker.performance?.actual_output || 0,
        achievement_percentage: worker.performance?.achievement_percentage || 0,
        deviation: worker.performance?.deviation || 0,
        deviation_from_target: worker.performance?.deviation || 0,
        days_worked: worker.performance?.days_worked || 0,
        days_in_period: worker.performance?.days_in_period || 0,
        period_start: worker.period_start,
        period_end: worker.period_end,
        period_type: worker.period_type,
        risk_status: worker.risk_status,
        risk_reason: worker.risk_reason,
        riskLevel: 'high', // All are high risk from this API
        riskFactors: [worker.risk_reason || 'High Risk'],
        efficiency: worker.performance?.achievement_percentage?.toFixed(1) || '0.0',
        target: worker.daily_target || worker.performance?.period_target || 0
      }));

      const newData = {
        high_risk_workers: mappedWorkers,
        summary: result.summary || null,
        risk_criteria: result.risk_criteria || '',
      };

      // Check if data has changed using ref to avoid stale closure
      const hasChanged = hasDataChanged(currentDataRef.current, newData);

      // Update state
      setHighRiskWorkers(newData.high_risk_workers);
      setSummary(newData.summary);
      setRiskCriteria(newData.risk_criteria);
      
      // Update ref with new data
      currentDataRef.current = newData;

      // Save to cache
      saveToCache(periodType, newData.high_risk_workers, newData.summary, newData.risk_criteria);

      // Show toast if data changed in background
      if (isBackground && hasChanged) {
        showToast('Risk data updated with latest information', 'success');
      }
    } catch (error) {
      if (!isBackground) {
        showToast(error.message || 'Failed to fetch risk data', 'error');
      }
      console.error('Error:', error);
      // Don't clear data on error, keep showing cached data
    } finally {
      if (showLoader) {
        setIsLoading(false);
      } else {
        setIsBackgroundLoading(false);
      }
    }
  };

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000);
  };

  // Keep ref in sync with state
  useEffect(() => {
    currentDataRef.current = {
      high_risk_workers: highRiskWorkers,
      summary: summary,
      risk_criteria: riskCriteria,
    };
  }, [highRiskWorkers, summary, riskCriteria]);

  // Fetch data on mount and when filters change
  useEffect(() => {
    const key = getCacheKey(periodType);
    setCacheKey(key);
    
    // Try to load cached data first
    const hasCachedData = loadCachedData(periodType);
    
    if (hasCachedData) {
      // Show cached data immediately, then fetch fresh data in background
      // Use setTimeout to ensure cached data is displayed first
      setTimeout(() => {
        fetchRiskData(false, true);
      }, 100);
    } else {
      // No cache, fetch with loader
      fetchRiskData(true, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType]);

  // Filter workers by search name
  const filteredWorkers = highRiskWorkers.filter(worker => {
    const name = worker.name || `${worker.first_name || ''} ${worker.last_name || ''}`.trim();
    return name.toLowerCase().includes(searchName.toLowerCase());
  });

  // Pagination
  const totalPages = Math.ceil(filteredWorkers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedWorkers = filteredWorkers.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [periodType, searchName]);

  // Get risk level color
  const getRiskColor = (level) => {
    switch (level) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };


  // Handle refresh
  const handleRefresh = () => {
    // Clear cache and fetch fresh data
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
    fetchRiskData(true, false);
  };

  // Handle export
  const handleExport = () => {
    try {
      const headers = ['Worker Name', 'Card Number', 'Group', 'Risk Level', 'Risk Factors', 'Actual Output', 'Target', 'Period Target', 'Deviation from Target', 'Days in Period', 'Days Worked', 'Period Start', 'Period End'];
      const rows = filteredWorkers.map(worker => {
        return [
          worker.name,
          worker.card_number || worker.id,
          worker.group_name || worker.group || 'N/A',
          worker.risk_status || 'HIGH RISK',
          worker.risk_reason || worker.riskFactors?.join('; ') || 'High Risk',
          (worker.actual_output || 0).toFixed(1),
          (worker.daily_target || 0).toFixed(1),
          (worker.period_target || 0).toFixed(1),
          (worker.deviation || worker.deviation_from_target || 0).toFixed(1),
          worker.days_in_period || 0,
          worker.days_worked || 0,
          worker.period_start || 'N/A',
          worker.period_end || 'N/A'
        ];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `risk_report_${formatDateOnly(new Date())}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showToast('Risk report exported successfully', 'success');
    } catch (err) {
      showToast('Failed to export report', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/manualpeeling')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <MdArrowBack className="text-xl" />
              <span>Back to Manual Peeling</span>
            </button>
            {isBackgroundLoading && (
              <div className="flex items-center text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">
                <MdRefresh className="mr-2 animate-spin" />
                <span>Updating...</span>
              </div>
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-800">Worker Risk Report</h1>
        </div>
        <p className="text-gray-600 mb-6">
          Identify workers at high risk based on performance metrics, attendance, and efficiency
        </p>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Period Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MdFilterList className="inline mr-2 text-purple-500" />
                Period Type
              </label>
              <select
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
              >
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MdSearch className="inline mr-2 text-blue-500" />
                Search Worker
              </label>
              <div className="relative">
                <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                {searchName && (
                  <button
                    onClick={() => setSearchName('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <MdRefresh className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={handleExport}
              className="px-6 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center font-medium"
            >
              <MdDownload className="mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Risk Criteria Info */}
        {riskCriteria && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded">
            <div className="flex items-start">
              <MdWarning className="text-yellow-600 mr-3 text-xl mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-yellow-800 mb-1">Risk Criteria</p>
                <p className="text-sm text-yellow-700">{riskCriteria}</p>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Workers Checked</p>
                  <p className="text-3xl font-bold text-gray-900">{summary.workers_checked || summary.total_eligible_workers || 0}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <MdPerson className="text-2xl text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">High Risk Workers</p>
                  <p className="text-3xl font-bold text-red-600">{summary.high_risk_workers_count || 0}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <MdWarning className="text-2xl text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Workers Skipped</p>
                  <p className="text-3xl font-bold text-gray-600">{summary.workers_skipped || 0}</p>
                </div>
                <div className="p-3 bg-gray-100 rounded-full">
                  <MdTrendingDown className="text-2xl text-gray-600" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Risk Workers Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center">
            <MdWarning className="mr-2 text-red-500" />
            Workers at Risk
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Worker
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Card Number
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Group
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Risk Level
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Risk Factors
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Actual Output
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Period Target
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Deviation from Target
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Days in Period
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Days Worked
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedWorkers.length === 0 ? (
                <tr>
                  <td colSpan="12" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <MdSearch className="text-6xl text-gray-300 mb-4" />
                      <p className="text-lg font-medium text-gray-600 mb-2">No workers found</p>
                      <p className="text-sm text-gray-500">
                        {searchName ? `No workers match "${searchName}"` : 'Try adjusting your filters'}
                      </p>
                      {searchName && (
                        <button
                          onClick={() => setSearchName('')}
                          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedWorkers.map((worker) => {
                  const workerId = worker.worker_id || worker.id;
                  const workerName = worker.name || `${worker.first_name || ''} ${worker.last_name || ''}`.trim() || 'Worker';
                  const cardNumber = worker.card_number || worker.id;
                  const groupName = worker.group_name || worker.group || 'N/A';

                  return (
                    <tr key={workerId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <MdPerson className="text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{workerName}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cardNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-800 rounded">
                          {groupName}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getRiskColor(worker.riskLevel || 'high')}`}>
                          {worker.risk_status || 'HIGH RISK'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex flex-wrap gap-1">
                          {worker.risk_reason ? (
                            <span className="inline-flex px-2 py-1 text-xs bg-red-50 text-red-700 rounded border border-red-200">
                              {worker.risk_reason}
                            </span>
                          ) : worker.riskFactors && worker.riskFactors.length > 0 ? (
                            worker.riskFactors.map((factor, index) => (
                              <span
                                key={index}
                                className="inline-flex px-2 py-1 text-xs bg-red-50 text-red-700 rounded border border-red-200"
                              >
                                {factor}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400 italic">High Risk</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(worker.actual_output || 0).toFixed(1)} kg
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(worker.daily_target || 0).toFixed(1)} kg
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(worker.period_target || 0).toFixed(1)} kg
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${
                          (worker.deviation || worker.deviation_from_target || 0) >= 0 ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
                        }`}>
                          {(worker.deviation || worker.deviation_from_target || 0) >= 0 ? '+' : ''}{(worker.deviation || worker.deviation_from_target || 0).toFixed(1)} kg
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {worker.days_in_period || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {worker.days_worked || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => navigate(`/admin/manualpeeling/worker/${workerId}`)}
                          className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-1"
                        >
                          <MdVisibility className="text-lg" />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredWorkers.length)} of {filteredWorkers.length} workers
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-blue-500 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {isLoading && <UsibrasLoader />}

      {/* Toast Notifications */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'info' })}
        />
      )}
    </div>
  );
};

export default RiskReport;

